import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import PercentFieldPreview from '@sprinx/react-mui-fields/PercentFieldPreview';
import TextFieldPreview from '@sprinx/react-mui-fields/TextFieldPreview';
import clsx from 'clsx';
import React from 'react';
import invariant from 'tiny-invariant';
import { ShoppingCartSubject } from '../../../api/shoppingCart';
import { CartShipmentPalletOnlyMark } from '../../../components/CartShipmentInfo';
import { useProductStockQuantities } from '../../../components/ProductStockProvider/ProductStockProvider';
import { showStockAvailability } from '../../../config';
import ProductStockQuantityInfo from '../../../components/ProductStockQuantityInfo';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

export interface CartSummarySubjectListItemInlineParametersProps
  extends StandardDivProps<CartSummarySubjectListItemInlineParametersClassKey> {
  subject: ShoppingCartSubject;
}

export type CartSummarySubjectListItemInlineParametersClassKey =
  | 'root'
  | 'field'
  | 'stock'
  | 'fixWidth'
  | 'productCode'
  | 'productType'
  | 'palletOnly';

const themeSettings = { name: 'CartSummarySubjectListItemInlineParameters' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<CartSummarySubjectListItemInlineParametersClassKey, {}>({
      root: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
        },
        [theme.breakpoints.down('sm')]: {
          paddingBottom: theme.spacing(1.5),
        },
      },
      field: {
        [theme.breakpoints.down('md')]: {
          flexDirection: 'row',
          alignItems: 'center',
        },
        [theme.breakpoints.down('sm')]: {
          margin: 0,
          '& div': {
            padding: 0,
          },
        },
      },
      fixWidth: {
        width: 120,
        display: 'flex',
      },
      productCode: {
        width: 85,
        '@media(max-width: 1279px)': {
          width: 120,
        },
      },
      productType: {
        width: 35,
        '@media(max-width: 1279px)': {
          width: 120,
        },
      },
      palletOnly: {
        marginLeft: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
          margin: theme.spacing(0.5, 0),
        },
      },
      stock: {
        textAlign: 'right',
        flexGrow: 2,
      },
    }),
  themeSettings,
);

const CartSummarySubjectListItemInlineParameters = React.forwardRef<
  HTMLDivElement,
  CartSummarySubjectListItemInlineParametersProps
>(({ className, classes: pClasses, subject }, ref) => {
  const classes = useStyles({ classes: pClasses });
  const quantities = useProductStockQuantities();
  const t = useTranslateWithFallback();

  invariant(typeof subject.product !== 'string');

  return (
    <div ref={ref} className={clsx(classes.root, className)}>
      <TextFieldPreview
        className={clsx(classes.field, classes.productCode)}
        label={t('common/code')}
        value={subject.product.sku || ''}
        size='small'
      />
      {subject.taxInfo && (
        <PercentFieldPreview
          className={clsx(classes.field, classes.fixWidth)}
          label={t('common/vat')}
          value={subject.taxInfo.value}
          size='small'
          align='right'
        />
      )}
      <CartShipmentPalletOnlyMark className={classes.palletOnly} product={subject.product} />
      {showStockAvailability && (
        <div className={classes.stock}>
          <ProductStockQuantityInfo product={{ stockQuantity: quantities && quantities[subject?.product?.id] }} />
        </div>
      )}
    </div>
  );
});

CartSummarySubjectListItemInlineParameters.displayName = 'CartSummarySubjectListItemInlineParameters';

export default withThemeProps(themeSettings)(CartSummarySubjectListItemInlineParameters);
